// How the WebGL2 client gets into a world (R502).
//
// It knows the four ways in the shipping client knows, and it decides
// between them with one table:
//
// | page URL      | `GET /api/me`             | what happens                 |
// | ------------- | ------------------------- | ---------------------------- |
// | has `server=` | never called              | direct connect               |
// | no `server=`  | fetch failed, or not JSON | direct connect               |
// | no `server=`  | 401                       | landing screen, then the hub |
// | no `server=`  | 200, `?world=` is a seat  | join that world, no menu     |
// | no `server=`  | 200 (guest or registered) | the hub                      |
// | no `server=`  | any other failure         | landing, with the message    |
// | has `imp=`    | before `GET /api/me`      | redeem the code, then re-read|
//
// The rule that matters is the same as the shipping client's: `?server=` is
// always direct, and a meta API that does not answer is direct too. A
// broken fetch must never lock a developer out of a world that is running.
//
// The `imp=` row is the operator's way in (deployment.md, the impersonation
// section). It is not a fifth way into a world: it changes WHO this browser
// is and then the four rows above decide as they always do. It runs before
// `GET /api/me`, so that call already answers as the player, and it runs
// only in the meta lane — a `?server=` direct connect has no meta server to
// redeem the code against. A refused code is a `notice` and nothing more:
// the operator lands on the screen the session they still hold deserves.
//
// The landing screen resolves one of two things (`ui/landing.ts`, R229): a
// **join**, when a visitor pressed a console and played as a guest, or the
// **account** it signed in, which goes on to the hub with no reload. The
// hub always resolves a join. Going back to the menu from a world is a
// navigation to this page without `world` and `server` (`actions.ts`, R236,
// R1203), and a sign-out is a page reload.
//
// A meta API call that waits longer than `API_TIMEOUT_MS` fails with
// `api_timeout` (R1203). That is the "any other failure" row, never the
// "fetch failed" row: a slow meta server is still a meta server.
//
// The page address carries one more thing since R621: a sign-in with Apple
// or with Google comes back to this origin, and the callback states its
// outcome in `signin` and `tab`. This file maps the code to a sentence,
// hands it to whichever screen opens as its `notice`, opens the hub on the
// named section, and takes both parameters off the address bar. Since
// R1649 `page` can name a page inside that section: a clan post about the
// clan cup links `?tab=clan&page=cup`, and `page` leaves the bar with `tab`.
// Since R1659 the landing screen hands `tab` and `page` to the start route
// of a provider, and the callback writes them back, so the deep link
// survives the trip to the provider as well.
//
// The screens are DOM and Canvas 2D, so this file adds no renderer to the
// bundle. `nopixi.test.ts` is what says so.

import { oneOf } from '../util/oneof';
import {
  ApiError, CODE_ALREADY_LINKED, CODE_IDENTITY_TAKEN, CODE_NAME_TAKEN, messageOf,
  CODE_SIGN_IN_METHOD_OFF, CODE_SIGN_IN_REFUSED, CODE_SIGN_IN_STATE, CODE_REAUTH_REQUIRED,
  getMe, impersonate, join as apiJoin, type Join, type JoinRequest, type Me,
} from '../net/api';
import type { ConnectTarget } from '../net/connection';
import type { AudioDirector } from '../audio/director';
import { LIGHT } from '../identity/pick';
import { FALLBACK_FACTION } from '../ui/theme';
import { lightFactionBody, wearLightOn } from '../ui/lightchrome';
import {
  FORGOT_PARAM, readForgot, showLanding, type LandingOptions, type LandingResult,
} from '../ui/landing';
import { showHub, type HubOptions } from '../ui/hub/hub';
import { hubFaction } from '../ui/hub/hubstate';
import {
  PAGE_CHRONICLE, PAGE_PARAM, TAB_KEYS, TAB_PAGES, type PageKey, type TabKey,
} from '../ui/hub/panel';
import { BOOK_KEY, FILM_KEY, readBookReturn, type FilmMiss } from '../ui/hub/chronicle/address';
import { markReauth } from '../ui/hub/panels/reauth';
import { lobbySection, readLobbyParam } from '../ui/hub/panels/customlobby';
import { LOBBY_PARAM } from '../ui/hublink';
// The gold strip an operator reads while they play a player's account. It
// is raised here, and not by the boot, because the hub and the landing
// screen both run INSIDE `resolveEntry`: a strip raised on the entry it
// resolves would only reach the glass at sea. `main.ts` mounts the root
// before this call and the module holds the one instance, the way
// `ui/chart/cartouche.ts` holds the banner's flag.
import { showImpBar } from '../ui/impbar';
import { copyLegacyAdvisor, loadSettings, pullSettings } from '../ui/settings';
import { ensureThemeBodies, missingThemeBodies, wearTheme } from '../ui/themestate';
import { chronicle as C, fast as S, LOCALE } from '../gen/strings';
import { applyDocumentLanguage } from '../locale/pick';

// The picked language goes on the document at boot (R855), so the browser
// hyphenates and a screen reader speaks the words of the right language.
applyDocumentLanguage(LOCALE);

/** How this client reached its world. */
export type EntryMode = 'direct' | 'meta';

export interface Entry {
  mode: EntryMode;
  target: ConnectTarget;
  /** The world the target names, or 0 in direct mode. */
  worldId: number;
  /**
   * The world's GAME mode, as `modes.toml` spells it: `core`, `event`,
   * `clanwar`, `arcade`. It is empty in direct mode, where no meta server
   * named one. The achievement record's season scope reads it (R951): the
   * hello carries the mode as a NUMBER and the service keys a season total
   * on the word.
   */
  worldMode: string;
}

/** A way in that failed before any renderer existed. The boot's catch
 *  reads the message and `showStop` writes it. */
export class NoEntry extends Error {}

/** The page query, read once. `main.ts` reads its own parameters off it. */
export const PAGE_PARAMS = new URLSearchParams(location.search);

/** The websocket scheme follows the page: an https page opens wss. */
const WS_SCHEME = location.protocol === 'https:' ? 'wss' : 'ws';
const SERVER_OVERRIDE = PAGE_PARAMS.get('server');
const SERVER = SERVER_OVERRIDE ?? `${WS_SCHEME}://${location.hostname}:8090/ws`;

/**
 * The collaborators `resolveEntry` calls, so the decision table can be
 * tested row by row without a network, a meta server or a screen.
 *
 * `entry.test.ts` passes its own; the client passes none and gets these.
 */
export interface EntryDeps {
  /** The page query. `server` and `world` are read from it. */
  params: URLSearchParams;
  /** The direct target `?server=` names, or the page host's game server. */
  server: string;
  getMe: () => Promise<Me>;
  /** Spends the one-time code of `?imp=`. It runs before `getMe`, so
   *  `getMe` answers as the account the operator asked for. */
  redeemImp: (code: string) => Promise<unknown>;
  join: (req: JoinRequest) => Promise<Join>;
  landing: (opts: LandingOptions) => Promise<LandingResult>;
  hub: (opts: HubOptions) => Promise<Join>;
  /** `account` is the signed-in account's public id, or '' where the
   *  caller knows none. It decides whether an unsent local write wins over
   *  the account copy (R820). */
  pullSettings: (account?: string) => Promise<unknown>;
}

/** What the browser hands `resolveEntry` when the caller names nothing. */
export function pageDeps(): EntryDeps {
  return {
    params: PAGE_PARAMS,
    server: SERVER,
    getMe,
    redeemImp: impersonate,
    join: apiJoin,
    landing: showLanding,
    hub: showHub,
    pullSettings,
  };
}

/**
 * What a sign-in with a provider left on the page address (R621).
 *
 * A callback of `/api/auth/oauth/<provider>/callback` answers a 303 and
 * never JSON, because the browser is on a top-level navigation. The outcome
 * therefore arrives as two page parameters:
 *
 *   - `signin=<code>` is a refusal. The client owns the sentence, because
 *     the server wrote none: the redirect carries a code alone.
 *   - `tab=settings` is where a link on a registered account comes back to,
 *     so the person lands on the row they pressed.
 *   - `tab` and `page` of a sign-in are the deep link the landing screen
 *     carried through the provider (R1659), with a refusal too.
 */
export interface SignInReturn {
  /** The sentence for the code, or an empty string when the address named
   *  no code. It is a `notice` on whichever screen opens. */
  notice: string;
  /** The section the hub opens on, or undefined when the address named
   *  none this hub knows. */
  tab?: TabKey;
  /** The page inside that section, or undefined when the address named
   *  none the section knows (`TAB_PAGES`, R1649). */
  page?: PageKey;
  /** The world whose live volume the chronicle book opens on. Only the
   *  chronicle page carries it (R1684). */
  book?: number;
  /** True when a link met `reauth_required` (R2963): the ACCOUNT page
   *  then shows SIGN IN AGAIN. */
  reauth?: boolean;
  /** The section word of a custom lobby that `?lobby=<code>` named
   *  (`lobby` and the code in lower case, §16.17): the hub opens PLAY on
   *  that lobby, and a sign-in with a provider carries the word. */
  section?: string;
}

/** The sentence of every `signin` code this client knows. */
const SIGN_IN_SENTENCES: ReadonlyMap<string, string> = new Map([
  [CODE_IDENTITY_TAKEN, S.signin_identity_taken],
  [CODE_SIGN_IN_STATE, S.signin_state],
  [CODE_SIGN_IN_REFUSED, S.signin_refused],
  [CODE_SIGN_IN_METHOD_OFF, S.signin_method_off],
  [CODE_ALREADY_LINKED, S.signin_already_linked],
  [CODE_NAME_TAKEN, S.signin_name_taken],
  [CODE_REAUTH_REQUIRED, S.signin_reauth],
]);

/** The notice of a replay link that played no film, on the book it went
 *  back to (R1684). */
const FILM_MISS: Readonly<Record<FilmMiss, string>> = {
  gone: C.replay_link_gone,
  silent: S.replay_link_silent,
};

/**
 * The sentence one `signin` code says.
 *
 * A code this client does not know still gets a sentence with the code in
 * it. A newer server may answer with a code this build predates, and a
 * blank screen is worse than a word a person can quote to support.
 */
function signInSentence(code: string): string {
  return SIGN_IN_SENTENCES.get(code) ?? S.signin_unknown({ code });
}

/** Reads the sign-in parameters and the page of a section off a page
 *  address. */
export function readSignIn(params: URLSearchParams): SignInReturn {
  const code = params.get('signin') ?? '';
  const want = params.get('tab') ?? '';
  // A link to a custom lobby opens PLAY on the lobby (R3088). It outranks
  // any other section word, because the person was sent to one lobby.
  const lobby = readLobbyParam(params);
  // A section this hub does not know is dropped rather than carried: the
  // hub would open on nothing at all.
  const tab = lobby !== '' ? 'play' : oneOf(TAB_KEYS, want);
  // A page belongs to one section, and a page that section does not list
  // is dropped the same way (R1649).
  const wantPage = params.get(PAGE_PARAM) ?? '';
  const page = tab === undefined ? undefined : oneOf(TAB_PAGES[tab] ?? [], wantPage);
  // The way back from a replay link names the volume, and the notice of a
  // film that never came (R1684).
  const ret = readBookReturn(params);
  const notice = code !== '' ? signInSentence(code) : ret.miss === undefined ? '' : FILM_MISS[ret.miss];
  return {
    notice, tab, page, book: page === PAGE_CHRONICLE ? ret.world : undefined,
    reauth: code === CODE_REAUTH_REQUIRED || undefined,
    section: lobby !== '' ? lobbySection(lobby) : undefined,
  };
}

/** The one-time impersonation code the address carries, or an empty string
 *  when it carries none (deployment.md, the impersonation section). */
function readImp(params: URLSearchParams): string {
  return (params.get('imp') ?? '').trim();
}

/**
 * Takes the sign-in parameters, the page of a section and the impersonation
 * code back off the address bar, and keeps every other one.
 *
 * A notice states the outcome of one press. A reload must not say it again,
 * and a shared address must not carry somebody else's refusal. A reload
 * opens the plain section, so `page` leaves with `tab` (R1649).
 *
 * `imp` leaves for a harder reason. It is a live credential for somebody
 * else's account, and it must not stand in an operator's address bar, in
 * their history or in a screenshot they paste into a ticket. So this runs
 * BEFORE the redemption and before any join can rewrite the address: the
 * code is off the bar whether the redemption goes on to work or not.
 *
 * The way back from a replay link (`book`, `film`) leaves for the same
 * reason as a notice (R1684), and so does `forgot`, the reset step of the
 * sign-in screen (R2088).
 *
 * All of them go in one `replaceState`, because two calls would put a second
 * entry-shaped record in the history for one arrival.
 */
function forgetSignIn(): void {
  const url = new URL(location.href);
  // `lobby` leaves too: the lobby page writes it back while it stands.
  const carried = ['signin', 'tab', PAGE_PARAM, BOOK_KEY, FILM_KEY, 'imp', FORGOT_PARAM, LOBBY_PARAM];
  if (!carried.some((key) => url.searchParams.has(key))) return;
  for (const key of carried) url.searchParams.delete(key);
  history.replaceState(null, '', url.toString());
}

/** Reads the `?world=` deep link. 0 means the page named no world. */
function deepLinkWorld(params: URLSearchParams): number {
  const raw = params.get('world');
  if (raw === null) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Writes the world the client is in back into the address bar, so a reload
 * and a shared link both land in the same world.
 */
export function rememberWorld(worldId: number): void {
  const url = new URL(location.href);
  if (url.searchParams.get('world') === String(worldId)) return;
  url.searchParams.set('world', String(worldId));
  history.replaceState(null, '', url.toString());
}

/** The join answer as a connect descriptor. The ws URL already carries
 *  `?world=<id>`, so the resume token lands under that world's key. */
export function fromJoin(j: Join): Entry {
  rememberWorld(j.world.id);
  return {
    mode: 'meta', target: { url: j.wsUrl, ticket: j.ticket },
    worldId: j.world.id, worldMode: j.world.mode,
  };
}

/** The way in with no meta server behind the origin: the game server, and no world named. */
function directEntry(deps: EntryDeps): Entry {
  return { mode: 'direct', target: { url: deps.server }, worldId: 0, worldMode: '' };
}

/**
 * Decides how this client gets into a world, and gets it there.
 *
 * The table at the head of this file is the whole rule. Every row that
 * shows a screen hands the screen the sound director, because the landing
 * and the hub own the menu music while they are up (audio.md §3.6, R227).
 */
export async function resolveEntry(
  audio?: AudioDirector, deps: EntryDeps = pageDeps(),
): Promise<Entry> {
  // What a sign-in with a provider came back with (R621). It is read
  // first and taken off the address at once, so a reload never says the
  // same thing a second time.
  const back = readSignIn(deps.params);
  // The impersonation code is read here and the address is cleared in the
  // same breath. The code is a credential for another person's account, so
  // it must leave the bar before anything else can happen — a failed
  // redemption, a join that rewrites the address, or an operator who
  // photographs the window for a ticket.
  const imp = readImp(deps.params);
  // The reset step of the sign-in screen (R2088). A session opens the hub
  // as it always does, and the word leaves the bar all the same.
  const forgot = readForgot(deps.params);
  forgetSignIn();
  // A link that met `reauth_required` (R2963): the SETTINGS panel takes the
  // mark when it opens and shows SIGN IN AGAIN.
  if (back.reauth) markReauth();

  const override = deps.params.get('server');
  if (override !== null) {
    // The direct lane speaks to a game server and never to a meta server,
    // so there is nothing here to redeem a code against. The code is spent
    // by nobody and expires on its own.
    return directEntry(deps);
  }

  let me: Me | null = null;
  let notice = back.notice;
  // The redemption comes before `getMe`, so `getMe` answers as the account
  // the operator asked for and every row below decides on the player's own
  // placements. A refusal is one sentence and no more: the cookie this
  // browser already holds is untouched, so the operator falls through to
  // the screen their own session deserves.
  if (imp !== '') {
    try {
      await deps.redeemImp(imp);
    } catch (err) {
      notice = messageOf(err);
    }
  }
  try {
    me = await deps.getMe();
  } catch (err) {
    if (err instanceof ApiError && err.unavailable) {
      // No meta server behind this origin: a plain `npx vite`, or a game
      // server a developer opened the page against. Behave as before.
      return directEntry(deps);
    }
    if (!(err instanceof ApiError) || err.status !== 401) {
      notice = messageOf(err);
    }
  }

  // The settings belong to the account (R256). The pull happens here,
  // where a session is known to exist and before anything is painted, so
  // the first frame already wears the banner the player stored — on this
  // browser or on any other. It never rejects and it never blocks for
  // long: a meta server that does not answer resolves to the local copy.
  // `pullSettings` does its work once per page load, so the two calls
  // below are one pull whichever way the player came in. The account id
  // lets a local write that never reached the account win over an older
  // account copy (R820): the theme workshop's "make theme active", written
  // just before the player came back here, is one.
  if (me) {
    await deps.pullSettings(me.account.id);
    // An advisor word an older client stored in the blob goes to the
    // account once, before the first join settles the gate (R1566).
    await copyLegacyAdvisor(me);
    // The custom theme bodies (themes.md §12, R814). `getMe` above stored
    // the entitlement set. A custom pick with no cached body at all waits
    // for its lookup, so the first frame wears it; a cached one refreshes
    // behind the screen that opens next.
    const s = loadSettings();
    if (missingThemeBodies(s)) await ensureThemeBodies(s);
    else void ensureThemeBodies(s);
  }

  // Whose session this is, said out loud, before any screen opens. The
  // strip stands over the landing screen, the hub and the map alike, so
  // this is the one place that has to raise it: every way in below already
  // knows the account, and none of them comes back until it has a world.
  showImpBar(me);

  // A deep link into a world this account already plays skips the screen:
  // the player asked for one world, and the answer is that world.
  const want = deepLinkWorld(deps.params);
  if (me && want && me.placements.some((p) => p.worldId === want)) {
    try {
      return fromJoin(await deps.join({ mode: 'core', world: want }));
    } catch (err) {
      notice = messageOf(err);
    }
  }

  // No session: the landing screen. It owns the menu music and the menu
  // cues while it is up (audio.md §3.6, R227), and it resolves either a
  // join — a visitor pressed a console and plays as a guest — or the
  // account it just signed in.
  if (!me) {
    // The deep link goes to the screen as well, because a provider button
    // leaves the page and must carry it through the provider (R1659).
    const out = await deps.landing({
      me: null, notice, world: want, audio, link: { tab: back.tab, page: back.page, section: back.section }, forgot,
    });
    // The screen signed somebody in, so there is an account to pull for.
    // A guest who pressed a console holds nothing yet, and the pull then
    // only arms the push, which is what makes that guest's first choice
    // reach the account it was made on. A local write made before the
    // sign-in never wins over the account copy, so the pull names the
    // account it read only when the screen answered one.
    await deps.pullSettings(out.kind === 'signedIn' ? out.me.account.id : '');
    if (out.kind === 'signedIn') await copyLegacyAdvisor(out.me);
    if (out.kind === 'join') return fromJoin(out.join);
    me = out.me;
    notice = '';
  }

  // A session, guest or registered: the hub. It keeps the menu music and
  // hands it back when it resolves a join.
  //
  // The hub wears its own banner (R242). AUTO here is the clan's faction
  // when the account is in a clan, and `account.faction` otherwise, which
  // is not what AUTO means in the game: at sea AUTO follows
  // `Hello.faction`, the faction worn in that world. A player who joined a
  // clan of another banner therefore wears the clan's colours in the menu
  // and keeps their own on the map for the rest of the running season.
  //
  // This call paints the header before the hub opens, so the first frame
  // is already right. The hub re-applies it whenever the account or the
  // clan changes, and never on a timer.
  // `tab` opens the hub on the section the address named. A link on a
  // sign-in provider comes back to `?tab=settings`, so the person lands on
  // the row they pressed and sees it turn to LINKED (R621). `page` opens a
  // page of that section: a cup post links the CUP page (R1649). Both were
  // read before the landing screen, so a sign-in there keeps them.
  wearTheme(hubFaction(me));
  return fromJoin(await deps.hub({
    me, notice, audio, tab: back.tab, page: back.page, book: back.book, section: back.section,
  }));
}

/**
 * The client's full-screen stop.
 *
 * The overlay owns the real one (`ui/overlay.ts` `fatal`), and it is not
 * built yet at either of the two moments this is needed: before the entry
 * resolves, and when the engine finds no WebGL2 context. So this is the
 * same shape, drawn by itself, and it writes no sentence of its own — the
 * words come from `gen/strings.ts` like every other word the DOM writes.
 * In the dark identity its inks are the two literals it always had, so the
 * dark look does not move (R3298, R3299). In the light identity it reads
 * the tokens (the deep ink at 0.92 and the text), and the box wears the
 * light body of the base faction, as the game chrome does before a theme
 * is worn (R3298).
 */
export function showStop(text: string): void {
  const box = document.createElement('div');
  box.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:100',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:8vw', 'box-sizing:border-box',
    ...(LIGHT
      ? ['background:rgb(var(--deep-rgb) / 0.92)', 'color:var(--text)']
      : ['background:rgba(1,6,12,0.92)', 'color:#cfe6f2']), // mask: dark only (R3298)
    'font:600 14px/1.7 ui-monospace,SFMono-Regular,Menlo,monospace',
    'letter-spacing:0.08em', 'text-align:center',
  ].join(';');
  if (LIGHT) wearLightOn(box, lightFactionBody(FALLBACK_FACTION));
  box.textContent = text;
  document.body.appendChild(box);
}
